import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons'
import { cva } from 'class-variance-authority'
import { ChevronDown, Loader2 } from 'lucide-react'
import * as React from 'react'
import { Virtuoso } from 'react-virtuoso'
import { cn } from '../lib/utils'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../primitives/command'
import { Label } from '../primitives/label'
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../primitives/tabs'
import { Tooltip } from '../primitives/tooltip'
import { Typography } from '../primitives/typography'

export interface SelectOptionWithTab {
  value: string
  label: string | React.ReactNode
  icon?: React.ReactNode
  /**
   * The value that will be used to filter the options
   * @default label
   * */
  searchValue?: string
  disabled?: boolean
  tooltipContent?: string
  tab: string
  [key: string]: unknown
}

export interface SelectWithTabsOptions {
  tabs: Array<{
    label: string
    value: string
  }>
  options: SelectOptionWithTab[]
}

export interface SelectWithTabsProps {
  name?: string
  label?: string | React.ReactNode
  selectConfig: SelectWithTabsOptions
  value?: string[] | string
  onChange?: (values: string[] | string) => void
  placeholder?: string
  inputPlaceholder?: string
  emptyPlaceholder?: string
  className?: string
  multiple?: boolean
  clearable?: boolean
  searchable?: boolean
  disabled?: boolean
  variant?: 'default' | 'ghost'
  popoverContentClassName?: string
  size?: 'sm' | 'md' | 'lg'
  helperText?: string
  error?: React.ReactNode
  loading?: boolean
  inline?: boolean
  // Remote select props
  onSearchChange?: (searchTerm: string) => void
  onLoadMore?: () => void
  onOpenChange?: (open: boolean) => void
  selectedValueRenderer?: React.ComponentType<{
    value: string[] | string
    options: SelectOptionWithTab[]
  }>
  /** Optional action element to render in the tabs header area */
  headerAction?: React.ReactNode
}

interface OptionItemProps {
  option: SelectOptionWithTab
  value: string[] | string | undefined
  handleSelect: (value: string) => void
  multiple: boolean
}

const OptionItem: React.FC<OptionItemProps> = ({ option, value, handleSelect, multiple }) => {
  const isSelected = Array.isArray(value) ? value.includes(option.value) : value === option.value

  const content = (
    <CommandItem
      key={option.value}
      onSelect={() => !option.disabled && handleSelect(option.value)}
      disabled={option.disabled}
      data-testid={option.value}
      className={cn(
        'min-h-11 rounded-md px-3 py-2 font-inter text-s-400 text-foreground aria-selected:bg-hv1',
        isSelected && 'bg-hv1',
        option.disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {multiple && (
        <div
          className={cn(
            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-st2',
            isSelected
              ? 'border-brand bg-brand text-brand-foreground'
              : 'opacity-70 [&_svg]:invisible',
          )}
        >
          <CheckIcon />
        </div>
      )}
      <span
        className="truncate flex items-center gap-2 whitespace-normal break-words"
        title={option.searchValue || (option.label as string)}
        data-test-value={option.value}
      >
        {option.icon} {option.label}
      </span>
      {!multiple && option.value === value && (
        <CheckIcon
          className={cn(
            'ml-auto size-4 shrink-0 text-brand-text',
            option.value === value ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
    </CommandItem>
  )

  if (option.disabled && option.tooltipContent) {
    return (
      <Tooltip content={option.tooltipContent} side="right">
        <div>{content}</div>
      </Tooltip>
    )
  }

  return content
}

interface MultiSelectValueProps {
  option: SelectOptionWithTab
  handleSelect: (value: string) => void
}

const MultiSelectValue: React.FC<MultiSelectValueProps> = ({ option, handleSelect }) => (
  <span className="inline-flex max-w-[150px] items-center gap-1 rounded-md border border-brand/20 bg-hv1 py-0.5 pl-2 pr-1 font-inter text-s-400 normal-case tracking-normal text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2">
    <span className="truncate max-w-full" title={option.searchValue || (option.label as string)}>
      {option.label}
    </span>
    <button
      aria-label={`Remove ${option.label}`}
      type="button"
      onClick={(e) => {
        e.preventDefault()
        handleSelect(option.value)
      }}
      className="flex items-center rounded-sm px-[1px] text-secondary hover:text-foreground focus-visible"
    >
      <Cross2Icon />
    </button>
  </span>
)

const selectStyles = cva('font-inter text-p-400 normal-case tracking-normal', {
  variants: {
    variant: {
      ghost: 'w-28',
      default: 'w-full',
    },
    size: {
      sm: 'min-h-[26px]',
      md: 'min-h-[36px]',
      lg: 'min-h-[42px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

export const SelectWithTabs = React.forwardRef<HTMLInputElement, SelectWithTabsProps>(
  (
    {
      inputPlaceholder,
      emptyPlaceholder,
      placeholder = 'Select agent, team or variables',
      className,
      selectConfig,
      value,
      label,
      onChange,
      multiple = false,
      disabled,
      clearable = false,
      searchable = true,
      variant = 'default',
      size = 'md',
      name,
      popoverContentClassName = 'w-[var(--radix-popover-trigger-width)]',
      helperText = '',
      error,
      loading = false,
      inline = false,
      // Remote select props
      onSearchChange, // this means search is remote
      onLoadMore,
      onOpenChange,
      selectedValueRenderer: SelectedValueRenderer,
      headerAction,
    },
    ref,
  ) => {
    const [searchTerm, setSearchTerm] = React.useState<string>('')
    const [isOpenInternal, setIsOpenInternal] = React.useState(false)
    const [activeTab, setActiveTab] = React.useState(selectConfig.tabs[0]?.value || '')

    // Use external handlers if provided
    const triggerButtonRef = React.useRef<HTMLButtonElement>(null)

    const handleSetIsOpen = (open: boolean) => {
      setIsOpenInternal(open)
      onOpenChange?.(open)
    }

    const handleSearchChange = (term: string) => {
      setSearchTerm(term)
      onSearchChange?.(term)
    }

    // Filter options based on search term (only if no remote search)
    const getFilteredOptionsForTab = (tabValue: string) => {
      const tabOptions = selectConfig.options.filter((option) => option.tab === tabValue)
      return onSearchChange
        ? tabOptions
        : tabOptions.filter((option) => {
            const haystack = (
              typeof option.label === 'string' ? option.label : (option.searchValue ?? '')
            ).toLowerCase()
            return haystack.includes(searchTerm.toLowerCase())
          })
    }
    // Handle selection
    const handleSelect = (selectedValue: string) => {
      if (!selectedValue) return // Guard against empty values

      if (multiple && Array.isArray(value)) {
        const newValue = value.includes(selectedValue)
          ? value.filter((v) => v !== selectedValue)
          : [...value, selectedValue]
        onChange?.(newValue)
      } else {
        onChange?.(multiple ? [selectedValue] : selectedValue)
        if (!multiple) {
          handleSetIsOpen(false)
        }
      }
      if (!onSearchChange) {
        setSearchTerm('')
      }
    }

    // Handle clearing selection
    const handleClear = () => {
      onChange?.(multiple ? [] : '')
    }

    const displayMultipleValues = multiple && Array.isArray(value) && value.length > 0
    // Preserve selection order by mapping value array to options
    const selectedOptions = Array.isArray(value)
      ? value
          .map((val) => selectConfig.options.find((option) => option.value === val))
          .filter((option): option is SelectOptionWithTab => Boolean(option))
      : []

    // Render selected values or placeholder based on conditions
    let content: React.ReactNode
    if (displayMultipleValues) {
      // Multiple selection
      if (SelectedValueRenderer) {
        content = <SelectedValueRenderer value={value} options={selectConfig.options} />
      } else {
        content = selectedOptions.map((option) => (
          <MultiSelectValue key={option.value} option={option} handleSelect={handleSelect} />
        ))
      }
    } else if (value && !multiple) {
      // Single selection - use custom renderer if provided
      if (SelectedValueRenderer) {
        content = <SelectedValueRenderer value={value} options={selectConfig.options} />
      } else {
        const selectedOption = selectConfig.options.find((opt) => opt.value === value)
        // Only show if we have a valid selected option
        content = selectedOption ? (
          <span className="flex items-center gap-2 min-w-0">
            {selectedOption.icon}
            <span className="truncate">{selectedOption.label}</span>
          </span>
        ) : (
          <span className="flex items-center gap-2 min-w-0 text-status-danger">
            <span className="truncate">{value}</span>
          </span>
        )
      }
    } else {
      content = <span className="mr-auto text-muted-foreground">{placeholder}</span>
    }

    const dataState = () => {
      if (loading) return 'closed'
      if (isOpenInternal) return 'open'
      return 'closed'
    }

    return (
      <Popover open={isOpenInternal} onOpenChange={handleSetIsOpen} modal data-state={dataState()}>
        <PopoverTrigger
          asChild
          className={cn(disabled && 'pointer-events-none', loading && 'pointer-events-none')}
        >
          <div className={cn(selectStyles({ variant, size }), className)}>
            {label && (
              <Label className="mb-1.5 block" htmlFor={name}>
                {label}
              </Label>
            )}
            <button
              ref={triggerButtonRef}
              type="button"
              id={name}
              role="combobox"
              aria-controls={name ? `${name}-content` : undefined}
              aria-expanded={isOpenInternal}
              className={cn(
                'flex w-full cursor-pointer items-center px-3 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/20',
                selectStyles({ size }),
                {
                  'justify-end border-none focus:ring-0 w-20': variant === 'ghost',
                  'justify-between rounded-md border border-st2 bg-background hover:border-brand/60':
                    variant === 'default',
                },
                disabled || loading ? 'bg-bg3 text-muted-foreground pointer-events-none' : '',
                (disabled || loading) && variant === 'ghost' ? 'bg-transparent text-tertiary' : '',
                isOpenInternal && variant !== 'ghost' && 'border-brand ring-2 ring-brand/20',
                error && 'border-destructive',
                className,
              )}
            >
              <div
                className={cn(
                  'items-center gap-1 overflow-hidden',
                  multiple ? 'flex flex-grow flex-wrap' : 'truncate',
                )}
              >
                {content}
              </div>

              {/* Icons section */}
              {!inline && (
                <div className="flex items-center pl-1 text-secondary transition-colors hover:text-foreground">
                  {loading && <Loader2 className="size-4 animate-spin" />}
                  {!loading && clearable && (Array.isArray(value) ? value.length > 0 : !!value) && (
                    <button
                      aria-label="Clear"
                      data-testid="button-clear"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        handleClear()
                      }}
                    >
                      <Cross2Icon className="size-4" />
                    </button>
                  )}
                  {!loading &&
                    !(clearable && (Array.isArray(value) ? value.length > 0 : !!value)) && (
                      <ChevronDown className="size-4" />
                    )}
                </div>
              )}
            </button>

            {helperText && (
              <Typography variant="XS/400" className="text-tertiary mt-2">
                {helperText}
              </Typography>
            )}
            {error && (
              <Typography variant="XS/400" className="text-status-danger mt-2">
                {error}
              </Typography>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            triggerButtonRef.current?.focus()
          }}
          id={name ? `${name}-content` : undefined}
          className={cn(
            'overflow-hidden rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-bs1',
            popoverContentClassName,
          )}
          align="start"
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-11 w-full rounded-none border-b border-border bg-transparent px-3">
              <div className="flex w-full items-center justify-between">
                <div className="flex">
                  {selectConfig.tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="flex h-11 justify-center rounded-none border-b-2 border-transparent px-3 font-inter text-s-500 normal-case tracking-normal text-secondary shadow-none data-[state=active]:border-brand data-[state=active]:text-foreground data-[state=active]:shadow-none"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </div>
                {headerAction}
              </div>
            </TabsList>
            <Command shouldFilter={false}>
              {searchable && (
                <div className="relative">
                  <CommandInput
                    value={searchTerm}
                    onValueChange={handleSearchChange}
                    ref={ref}
                    placeholder={inputPlaceholder ?? 'Search...'}
                    className="h-10 font-inter text-s-400"
                  />
                  {searchTerm && (
                    <button
                      aria-label="Clear search"
                      type="button"
                      className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-muted-foreground hover:text-foreground"
                      onClick={() => handleSearchChange('')}
                    >
                      <Cross2Icon className="size-4" />
                    </button>
                  )}
                </div>
              )}
              <CommandList>
                <CommandEmpty>{emptyPlaceholder ?? 'No results found.'}</CommandEmpty>
                {selectConfig.tabs.map((tab) => {
                  const filteredOptions = getFilteredOptionsForTab(tab.value)

                  // Virtuoso virtualized list if more than 6 options
                  const optionComponents =
                    filteredOptions.length > 6 ? (
                      <Virtuoso
                        itemContent={(_, option) => (
                          <OptionItem
                            key={option.value}
                            option={option}
                            value={value}
                            handleSelect={handleSelect}
                            multiple={multiple || false}
                          />
                        )}
                        data={filteredOptions}
                        style={{ height: 300 }}
                        endReached={onLoadMore}
                      />
                    ) : (
                      filteredOptions.map((option) => (
                        <OptionItem
                          key={option.value}
                          option={option}
                          value={value}
                          handleSelect={handleSelect}
                          multiple={multiple || false}
                        />
                      ))
                    )

                  return (
                    <TabsContent key={tab.value} value={tab.value} className="mt-0">
                      <CommandGroup>{optionComponents}</CommandGroup>
                    </TabsContent>
                  )
                })}
              </CommandList>
            </Command>
          </Tabs>
        </PopoverContent>
      </Popover>
    )
  },
)

SelectWithTabs.displayName = 'SelectWithTabs'
